export type Lang = 'ar' | 'en';

export interface Translations {
  common: {
    loading: string; save: string; cancel: string; delete: string; edit: string;
    back: string; confirm: string; error: string; success: string;
    search: string; noData: string; actions: string;
  };
  auth: {
    loginSubtitle: string; email: string; emailPlaceholder: string; password: string;
    forgotPassword: string; login: string; loggingIn: string; logout: string;
    myAccount: string; profile: string; wallet: string; forgotPasswordTitle: string;
    forgotPasswordSubtitle: string; sendResetLink: string; sending: string;
    backToLogin: string; resetEmailSent: string; checkYourEmail: string;
  };
  nav: {
    dashboard: string; shipments: string; drivers: string; map: string;
    tenants: string; reports: string; finance: string; users: string;
    settings: string; carriers: string; warehouses: string; support: string;
    barcode: string; fulfillmentCenters: string; manifests: string;
    manifestsAll: string; manifestsPickupRun: string; manifestsDeliveryRun: string;
    manifestsTransferRun: string; manifestsHubInbound: string; manifestsHubOutbound: string;
    manifestsReturnPickupRun: string; manifestsReturnDeliveryRun: string;
    trips: string;
    interBranchTransfer: string; dailyOperations: string; financials: string;
    customers: string; deliveryAgents: string; accounts: string; storage: string;
    coreSettings: string;
    home: string; fcDashboard: string; shipmentsList: string; createShipment: string;
    printBarcode: string; scanBarcode: string; createTransfer: string;
    transferHistory: string; todayOperations: string; archive: string;
    invoices: string; customerSettlements: string; driverCodSettlements: string;
    driverPaySettlements: string; outstandingFees: string; refunds: string; walletDeposit: string;
    financialReports: string; customersList: string; addCustomer: string;
    agentsList: string; addAgent: string; agentTracking: string;
    carriersList: string; addCarrier: string; usersList: string; permissions: string;
    citiesAndZones: string; coverage: string; deliveryPricing: string; deliveryZones: string;
    routes: string; warehousesList: string; systemAdmin: string;
    exceptionLogs: string; exceptionLogDetail: string; exceptionIncidentDetail: string;
  };
  breadcrumbs: Record<string, string>;
  footer: { rights: string };
  onboarding: {
    title: string;
    subtitle: string;
    progressLabel: string;
    setupComplete: string;
    setupCompleteDesc: string;
    goToDashboard: string;
    next: string;
    start: string;
    done: string;
    failedToLoad: string;
  };
  financials: {
    cod_settlements: string; pay_settlements: string; refunds: string;
    generate_cod_settlement: string; generate_pay_settlement: string; create_refund: string;
    unsettled_cod: string; unsettled_pay: string; pending_refunds: string;
    driver_balance: string; net: string; driver_owes_carrier: string; carrier_owes_driver: string;
    period_start: string; period_end: string; trips_count: string;
    base_amount: string; bonus_amount: string; penalty_amount: string; total_amount: string;
    add_adjustment: string; adjustment_type: string; amount: string; description: string;
    bonus: string; penalty: string; salary: string; adjustment: string; accrual: string;
    confirm_settlement: string; mark_paid: string; reverse_settlement: string; paid_via: string;
    approve_refund: string; reject_refund: string; refund_reason: string;
    customer_name: string; customer_phone: string; customer_iban: string; debtor: string;
    per_trip_bonus: string; commission_rate: string; payment_type: string;
    salary_type: string; commission_type: string;
    status_draft: string; status_confirmed: string; status_paid: string;
    status_reversed: string; status_approved: string; status_rejected: string;
    no_unsettled_cod: string; no_unsettled_pay: string; no_refunds_pending: string;
    settlement_generated: string; settlement_confirmed: string; settlement_paid: string;
    settlement_reversed: string; refund_created: string; refund_approved: string;
    refund_paid: string; refund_rejected: string;
  };
}

const ar: Translations = {
  common: {
    loading: 'جاري التحميل...', save: 'حفظ', cancel: 'إلغاء', delete: 'حذف', edit: 'تعديل',
    back: 'رجوع', confirm: 'تأكيد', error: 'خطأ', success: 'تم بنجاح',
    search: 'بحث', noData: 'لا توجد بيانات', actions: 'إجراءات',
  },
  auth: {
    loginSubtitle: 'تسجيل الدخول إلى لوحة التحكم',
    email: 'البريد الإلكتروني', emailPlaceholder: 'mohammed@flex.com',
    password: 'كلمة المرور', forgotPassword: 'نسيت كلمة المرور؟',
    login: 'تسجيل الدخول', loggingIn: 'جاري تسجيل الدخول...',
    logout: 'تسجيل الخروج', myAccount: 'حسابي', profile: 'الملف الشخصي', wallet: 'المحفظة',
    forgotPasswordTitle: 'نسيت كلمة المرور',
    forgotPasswordSubtitle: 'أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة تعيين كلمة المرور',
    sendResetLink: 'إرسال رابط الإعادة', sending: 'جاري الإرسال...',
    backToLogin: 'العودة لتسجيل الدخول',
    resetEmailSent: 'تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني',
    checkYourEmail: 'تحقق من بريدك الإلكتروني',
  },
  nav: {
    dashboard: 'لوحة التحكم', shipments: 'الشحنات', drivers: 'السائقون',
    map: 'الخريطة', tenants: 'التجار', reports: 'التقارير', finance: 'المالية',
    users: 'المستخدمون', settings: 'الإعدادات', carriers: 'شركات الشحن',
    warehouses: 'المستودعات', support: 'الدعم الفني',
    barcode: 'الباركود', fulfillmentCenters: 'مراكز الفرز', manifests: 'القوائم',
    manifestsAll: 'جميع القوائم', manifestsPickupRun: 'قائمة الالتقاط',
    manifestsDeliveryRun: 'قائمة التوصيل', manifestsTransferRun: 'قائمة النقل',
    manifestsHubInbound: 'قائمة الوارد', manifestsHubOutbound: 'قائمة الصادر',
    manifestsReturnPickupRun: 'قائمة استلام المرتجعات', manifestsReturnDeliveryRun: 'قائمة الإرجاع للمرسل',
    trips: 'الرحلات', interBranchTransfer: 'التحويل بين الفروع',
    dailyOperations: 'العمليات اليومية', financials: 'العمليات المالية',
    customers: 'العملاء', deliveryAgents: 'مندوبي التوصيل',
    accounts: 'الحسابات', storage: 'التخزين', coreSettings: 'التعريفات الأساسية',
    home: 'الرئيسية', fcDashboard: 'لوحة العمليات',
    shipmentsList: 'قائمة الشحنات', createShipment: 'إنشاء شحنة جديدة',
    printBarcode: 'طباعة الباركود', scanBarcode: 'قراءة الباركود',
    createTransfer: 'إنشاء تحويل', transferHistory: 'سجل التحويلات',
    todayOperations: 'عمليات اليوم', archive: 'الأرشيف',
    invoices: 'الفواتير', customerSettlements: 'تسويات العملاء',
    driverCodSettlements: 'تسويات النقدية للسائقين',
    driverPaySettlements: 'تسويات أجور السائقين',
    outstandingFees: 'الرسوم المستحقة من التجار',
    refunds: 'المرتجعات', walletDeposit: 'المحافظ — إيداع رصيد',
    financialReports: 'التقارير المالية',
    customersList: 'قائمة العملاء', addCustomer: 'إضافة عميل',
    agentsList: 'قائمة المندوبين', addAgent: 'إضافة مندوب', agentTracking: 'تتبع المندوبين',
    carriersList: 'قائمة شركات الشحن', addCarrier: 'إضافة شركة شحن',
    usersList: 'المستخدمين', permissions: 'الصلاحيات',
    citiesAndZones: 'المدن والمناطق', coverage: 'التغطية', deliveryPricing: 'أسعار التوصيل',
    deliveryZones: 'مناطق التوصيل', routes: 'المسارات', warehousesList: 'المستودعات',
    systemAdmin: 'مسؤول النظام',
    exceptionLogs: 'سجلات الأخطاء', exceptionLogDetail: 'تفاصيل الخطأ', exceptionIncidentDetail: 'تفاصيل الحادثة',
  },
  breadcrumbs: {
    dashboard: 'الرئيسية', shipments: 'الشحنات', drivers: 'السائقون',
    map: 'الخريطة', tenants: 'التجار', reports: 'التقارير', finance: 'المالية',
    users: 'المستخدمون', settings: 'الإعدادات', carriers: 'شركات الشحن',
    warehouses: 'المستودعات', support: 'الدعم',
    create: 'إنشاء', details: 'التفاصيل', pricing: 'قوائم الأسعار', cities: 'المدن', coverage: 'التغطية',
    'delivery-zones': 'مناطق التوصيل', routes: 'المسارات',
    'capture-lists': 'قوائم التحصيل', settlements: 'التسويات',
    barcode: 'الباركود', print: 'طباعة', scan: 'مسح', transfer: 'النقل',
    new: 'جديد', history: 'السجل', operations: 'العمليات', today: 'اليوم', archive: 'الأرشيف',
    accounts: 'الحسابات', permissions: 'الصلاحيات', storage: 'التخزين',
    onboarding: 'التهيئة', 'driver-tracking': 'تتبع السائقين', wallet: 'المحافظ',
    'fulfillment-centers': 'مراكز الفرز', manifests: 'البيانات', fc: 'مركز الفرز',
  },
  footer: { rights: 'جميع الحقوق محفوظة' },
  onboarding: {
    title: 'أكمل إعداد حسابك',
    subtitle: 'أنهِ {count} خطوات أدناه للبدء في استقبال الشحنات.',
    progressLabel: '{completed} من {total} مكتمل',
    setupComplete: 'اكتمل الإعداد!',
    setupCompleteDesc: 'حسابك كناقل أصبح نشطًا وجاهزًا لاستقبال الشحنات.',
    goToDashboard: 'الذهاب إلى لوحة التحكم',
    next: 'التالي', start: 'ابدأ', done: 'مكتمل', failedToLoad: 'فشل تحميل حالة الإعداد.',
  },
  financials: {
    cod_settlements: 'تسويات النقدية للسائقين', pay_settlements: 'تسويات أجور السائقين',
    refunds: 'المرتجعات', generate_cod_settlement: 'إنشاء تسوية نقدية',
    generate_pay_settlement: 'إنشاء تسوية أجر', create_refund: 'إنشاء مرتجع',
    unsettled_cod: 'النقدية غير المسوّاة', unsettled_pay: 'الأجور غير المسوّاة',
    pending_refunds: 'المرتجعات المعلّقة', driver_balance: 'رصيد السائق', net: 'الصافي',
    driver_owes_carrier: 'السائق مدين للناقل', carrier_owes_driver: 'الناقل مدين للسائق',
    period_start: 'بداية الفترة', period_end: 'نهاية الفترة', trips_count: 'عدد الرحلات',
    base_amount: 'المبلغ الأساسي', bonus_amount: 'الحوافز', penalty_amount: 'الخصومات', total_amount: 'الإجمالي',
    add_adjustment: 'إضافة تسوية', adjustment_type: 'نوع التسوية', amount: 'المبلغ', description: 'الوصف',
    bonus: 'حافز', penalty: 'خصم', salary: 'راتب', adjustment: 'تسوية', accrual: 'استحقاق',
    confirm_settlement: 'تأكيد التسوية', mark_paid: 'تحديد كمدفوعة', reverse_settlement: 'إلغاء التسوية',
    paid_via: 'وسيلة الدفع', approve_refund: 'اعتماد المرتجع', reject_refund: 'رفض المرتجع',
    refund_reason: 'سبب المرتجع', customer_name: 'اسم العميل', customer_phone: 'هاتف العميل', customer_iban: 'الآيبان',
    debtor: 'المدين', per_trip_bonus: 'حافز الرحلة', commission_rate: 'نسبة العمولة',
    payment_type: 'نوع الدفع', salary_type: 'راتب ثابت', commission_type: 'عمولة',
    status_draft: 'مسودة', status_confirmed: 'مؤكدة', status_paid: 'مدفوعة', status_reversed: 'مرتجعة',
    status_approved: 'معتمدة', status_rejected: 'مرفوضة',
    no_unsettled_cod: 'لا توجد نقدية غير مسوّاة', no_unsettled_pay: 'لا توجد أجور غير مسوّاة',
    no_refunds_pending: 'لا توجد مرتجعات معلّقة',
    settlement_generated: 'تم إنشاء التسوية بنجاح', settlement_confirmed: 'تم تأكيد التسوية',
    settlement_paid: 'تم تحديد التسوية كمدفوعة', settlement_reversed: 'تم إلغاء التسوية',
    refund_created: 'تم إنشاء المرتجع', refund_approved: 'تم اعتماد المرتجع',
    refund_paid: 'تم تحديد المرتجع كمدفوع', refund_rejected: 'تم رفض المرتجع',
  },
};

const en: Translations = {
  common: {
    loading: 'Loading...', save: 'Save', cancel: 'Cancel', delete: 'Delete', edit: 'Edit',
    back: 'Back', confirm: 'Confirm', error: 'Error', success: 'Success',
    search: 'Search', noData: 'No data available', actions: 'Actions',
  },
  auth: {
    loginSubtitle: 'Sign in to your dashboard',
    email: 'Email', emailPlaceholder: 'mohammed@flex.com', password: 'Password',
    forgotPassword: 'Forgot password?', login: 'Sign in', loggingIn: 'Signing in...',
    logout: 'Logout', myAccount: 'My Account', profile: 'Profile', wallet: 'Wallet',
    forgotPasswordTitle: 'Forgot Password',
    forgotPasswordSubtitle: "Enter your email and we'll send you a reset link",
    sendResetLink: 'Send reset link', sending: 'Sending...',
    backToLogin: 'Back to login',
    resetEmailSent: 'Password reset link has been sent to your email',
    checkYourEmail: 'Check your email',
  },
  nav: {
    dashboard: 'Dashboard', shipments: 'Shipments', drivers: 'Drivers', map: 'Map',
    tenants: 'Tenants', reports: 'Reports', finance: 'Finance', users: 'Users',
    settings: 'Settings', carriers: 'Carriers', warehouses: 'Warehouses', support: 'Support',
    barcode: 'Barcode', fulfillmentCenters: 'Fulfillment Centers', manifests: 'Lists',
    manifestsAll: 'All Lists', manifestsPickupRun: 'Pickup Run',
    manifestsDeliveryRun: 'Delivery Run', manifestsTransferRun: 'Transfer Run',
    manifestsHubInbound: 'Hub Inbound', manifestsHubOutbound: 'Hub Outbound',
    manifestsReturnPickupRun: 'Return Pickup Run', manifestsReturnDeliveryRun: 'Return Delivery Run',
    trips: 'Trips', interBranchTransfer: 'Inter-branch Transfer',
    dailyOperations: 'Daily Operations', financials: 'Financial Operations',
    customers: 'Customers', deliveryAgents: 'Delivery Agents',
    accounts: 'Accounts', storage: 'Storage', coreSettings: 'Core Settings',
    home: 'Home', fcDashboard: 'Operations Dashboard',
    shipmentsList: 'Shipments List', createShipment: 'Create New Shipment',
    printBarcode: 'Print Barcode', scanBarcode: 'Scan Barcode',
    createTransfer: 'Create Transfer', transferHistory: 'Transfer History',
    todayOperations: "Today's Operations", archive: 'Archive',
    invoices: 'Invoices', customerSettlements: 'Customer Settlements',
    driverCodSettlements: 'Driver COD Settlements', driverPaySettlements: 'Driver Pay Settlements',
    outstandingFees: 'Outstanding tenant fees',
    refunds: 'Refunds', walletDeposit: 'Wallets — Deposit Balance',
    financialReports: 'Financial Reports',
    customersList: 'Customer List', addCustomer: 'Add Customer',
    agentsList: 'Agents List', addAgent: 'Add Agent', agentTracking: 'Agent Tracking',
    carriersList: 'Carriers List', addCarrier: 'Add Carrier',
    usersList: 'Users', permissions: 'Permissions',
    citiesAndZones: 'Cities & Zones', coverage: 'Coverage', deliveryPricing: 'Delivery Pricing',
    deliveryZones: 'Delivery Zones', routes: 'Routes', warehousesList: 'Warehouses',
    systemAdmin: 'System Admin',
    exceptionLogs: 'Exception Logs', exceptionLogDetail: 'Log Detail', exceptionIncidentDetail: 'Incident Detail',
  },
  breadcrumbs: {
    dashboard: 'Home', shipments: 'Shipments', drivers: 'Drivers', map: 'Map',
    tenants: 'Tenants', reports: 'Reports', finance: 'Finance', users: 'Users',
    settings: 'Settings', carriers: 'Carriers', warehouses: 'Warehouses', support: 'Support',
    create: 'Create', details: 'Details', pricing: 'Pricing Lists', cities: 'Cities', coverage: 'Coverage',
    'delivery-zones': 'Delivery Zones', routes: 'Routes', 'capture-lists': 'Capture Lists',
    settlements: 'Settlements', barcode: 'Barcode', print: 'Print', scan: 'Scan',
    transfer: 'Transfer', new: 'New', history: 'History', operations: 'Operations',
    today: 'Today', archive: 'Archive', accounts: 'Accounts', permissions: 'Permissions',
    storage: 'Storage', onboarding: 'Onboarding', 'driver-tracking': 'Driver Tracking',
    wallet: 'Wallets', 'fulfillment-centers': 'Fulfillment Centers',
    manifests: 'Manifests', fc: 'Fulfillment Center',
  },
  footer: { rights: 'All rights reserved' },
  onboarding: {
    title: 'Complete Your Setup',
    subtitle: 'Finish the {count} steps below to start receiving shipments.',
    progressLabel: '{completed} of {total} completed',
    setupComplete: 'Setup Complete!',
    setupCompleteDesc: 'Your carrier account is now active and ready to receive shipments.',
    goToDashboard: 'Go to Dashboard',
    next: 'Go', start: 'Start', done: 'Done', failedToLoad: 'Failed to load onboarding status.',
  },
  financials: {
    cod_settlements: 'Driver COD Settlements', pay_settlements: 'Driver Pay Settlements',
    refunds: 'Refunds', generate_cod_settlement: 'Generate COD Settlement',
    generate_pay_settlement: 'Generate Pay Settlement', create_refund: 'Create Refund',
    unsettled_cod: 'Unsettled COD', unsettled_pay: 'Unsettled Pay',
    pending_refunds: 'Pending Refunds', driver_balance: 'Driver Balance', net: 'Net',
    driver_owes_carrier: 'Driver owes carrier', carrier_owes_driver: 'Carrier owes driver',
    period_start: 'Period Start', period_end: 'Period End', trips_count: 'Trips Count',
    base_amount: 'Base Amount', bonus_amount: 'Bonus', penalty_amount: 'Penalty', total_amount: 'Total',
    add_adjustment: 'Add Adjustment', adjustment_type: 'Adjustment Type',
    amount: 'Amount', description: 'Description', bonus: 'Bonus', penalty: 'Penalty',
    salary: 'Salary', adjustment: 'Adjustment', accrual: 'Accrual',
    confirm_settlement: 'Confirm Settlement', mark_paid: 'Mark Paid',
    reverse_settlement: 'Reverse Settlement', paid_via: 'Paid Via',
    approve_refund: 'Approve Refund', reject_refund: 'Reject Refund',
    refund_reason: 'Refund Reason', customer_name: 'Customer Name',
    customer_phone: 'Customer Phone', customer_iban: 'IBAN', debtor: 'Debtor',
    per_trip_bonus: 'Per-Trip Bonus', commission_rate: 'Commission Rate',
    payment_type: 'Payment Type', salary_type: 'Salary', commission_type: 'Commission',
    status_draft: 'Draft', status_confirmed: 'Confirmed', status_paid: 'Paid',
    status_reversed: 'Reversed', status_approved: 'Approved', status_rejected: 'Rejected',
    no_unsettled_cod: 'No unsettled COD', no_unsettled_pay: 'No unsettled pay',
    no_refunds_pending: 'No refunds pending',
    settlement_generated: 'Settlement generated successfully', settlement_confirmed: 'Settlement confirmed',
    settlement_paid: 'Settlement marked as paid', settlement_reversed: 'Settlement reversed',
    refund_created: 'Refund created', refund_approved: 'Refund approved',
    refund_paid: 'Refund marked as paid', refund_rejected: 'Refund rejected',
  },
};

export const translations: Record<Lang, Translations> = { ar, en };
